import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEdit, FiTrash2, FiArrowLeft, FiCalendar, FiTrendingUp, FiDollarSign } from 'react-icons/fi';
import forecastService from '../../services/forecastService';
import './Forecasts.css';
import { CURRENCY_SYMBOL } from '../../config/constants';

const ForecastView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const Inr = CURRENCY_SYMBOL

  useEffect(() => {
    loadForecast();
  }, [id]);

  const loadForecast = async () => {
    try {
      setLoading(true);
      const response = await forecastService.getById(id);
      setForecast(response.data);
    } catch (error) {
      console.error('Error loading forecast:', error);
      toast.error('Failed to load forecast');
      navigate('/app/forecasts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this forecast?')) {
      try {
        await forecastService.delete(id);
        toast.success('Forecast deleted successfully');
        navigate('/app/forecasts');
      } catch (error) {
        console.error('Error deleting forecast:', error);
        toast.error('Failed to delete forecast');
      }
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      Draft: 'status-draft',
      Active: 'status-active',
      Completed: 'status-completed',
      Archived: 'status-archived'
    };
    return statusClasses[status] || 'status-default';
  };

  const getConfidenceBadgeClass = (confidence) => {
    const confidenceClasses = {
      Low: 'confidence-low',
      Medium: 'confidence-medium',
      High: 'confidence-high',
      'Very High': 'confidence-very-high'
    };
    return confidenceClasses[confidence] || 'confidence-default';
  };

  if (loading) {
    return (
      <div className="forecasts-page">
        <div className="forecasts-loading">Loading forecast...</div>
      </div>
    );
  }

  if (!forecast) {
    return (
      <div className="forecasts-page">
        <div className="forecasts-empty">Forecast not found</div>
      </div>
    );
  }

  return (
    <div className="forecasts-page">
      <div className="forecasts-header">
        <div className="forecasts-header-left">
          <button
            className="btn-back"
            onClick={() => navigate('/app/forecasts')}
          >
            <FiArrowLeft />
            Back to Forecasts
          </button>
          <h1 className="forecasts-title">{forecast.title}</h1>
          <div className="forecast-meta">
            <span className={`status-badge ${getStatusBadgeClass(forecast.status)}`}>
              {forecast.status}
            </span>
            <span className={`confidence-badge ${getConfidenceBadgeClass(forecast.confidence_level)}`}>
              {forecast.confidence_level} Confidence
            </span>
            <span className="forecast-type-badge">{forecast.forecast_type}</span>
          </div>
        </div>
        <div className="forecasts-header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate(`/app/forecasts/${id}/edit`)}
          >
            <FiEdit />
            Edit
          </button>
          <button
            className="btn-danger"
            onClick={handleDelete}
          >
            <FiTrash2 />
            Delete
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="forecast-metrics grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 ">
        <div className="metric-card ">
          <div className="metric-icon custom-forecast-icon">
           <Inr />
          </div>
          <div className="metric-content ">
            <div className="metric-label">Forecasted Value</div>
            <div className="metric-value custom-metric-value">{formatCurrency(forecast.forecasted_value)}</div>
          </div>
        </div>

        {forecast.actual_value && (
          <div className="metric-card ">
            <div className="metric-icon custom-forecast-icon">
              <Inr />
            </div>
            <div className="metric-content ">
              <div className="metric-label">Actual Value</div>
              <div className="metric-value custom-metric-value">{formatCurrency(forecast.actual_value)}</div>
            </div>
          </div>
        )}

        {forecast.variance && (
          <div className="metric-card ">
            <div className="metric-icon custom-forecast-icon">
              <FiTrendingUp />
            </div>
            <div className="metric-content">
              <div className="metric-label">Variance</div>
              <div className={`metric-value custom-metric-value ${forecast.variance >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(forecast.variance)}
                {forecast.variance_percentage && (
                  <span className="metric-percentage">
                    ({forecast.variance_percentage}%)
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {forecast.accuracy && (
          <div className="metric-card ">
            <div className="metric-icon custom-forecast-icon">
              <FiTrendingUp />
            </div>
            <div className="metric-content ">
              <div className="metric-label">Accuracy</div>
              <div className="metric-value custom-metric-value">{forecast.accuracy}%</div>
            </div>
          </div>
        )}
      </div>

      {/* Forecast Details */}
      <div className="forecast-details-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-4 ">
        {/* Basic Information */}
        <div className="detail-section">
          <h3 className="detail-section-title">Basic Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label className="detail-label">Project</label>
              <div className="detail-value">
                {forecast.project ? (
                  <span
                    className="project-link"
                    onClick={() => navigate(`/app/projects/${forecast.project.id}`)}
                  >
                    {forecast.project.name}
                  </span>
                ) : (
                  '-'
                )}
              </div>
            </div>

            <div className="detail-item">
              <label className="detail-label">Forecast Type</label>
              <div className="detail-value">{forecast.forecast_type}</div>
            </div>

            <div className="detail-item">
              <label className="detail-label">Period Type</label>
              <div className="detail-value">{forecast.period_type}</div>
            </div>

            <div className="detail-item">
              <label className="detail-label">Forecast Date</label>
              <div className="detail-value">
                <FiCalendar className="detail-icon" />
                {formatDate(forecast.forecast_date)}
              </div>
            </div>

            {forecast.description && (
              <div className="detail-item full-width">
                <label className="detail-label">Description</label>
                <div className="detail-value">{forecast.description}</div>
              </div>
            )}
          </div>
        </div>

        {/* Values */}
        <div className="detail-section">
          <h3 className="detail-section-title">Values</h3>
          <div className="detail-grid">
            {forecast.baseline_value && (
              <div className="detail-item">
                <label className="detail-label">Baseline Value</label>
                <div className="detail-value">{formatCurrency(forecast.baseline_value)}</div>
              </div>
            )}

            <div className="detail-item">
              <label className="detail-label">Forecasted Value</label>
              <div className="detail-value">{formatCurrency(forecast.forecasted_value)}</div>
            </div>

            {forecast.actual_value && (
              <div className="detail-item">
                <label className="detail-label">Actual Value</label>
                <div className="detail-value">{formatCurrency(forecast.actual_value)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Methodology & Assumptions */}
        {(forecast.methodology || forecast.assumptions) && (
          <div className="detail-section full-width">
            <h3 className="detail-section-title">Methodology & Assumptions</h3>
            <div className="detail-grid">
              {forecast.methodology && (
                <div className="detail-item full-width">
                  <label className="detail-label">Methodology</label>
                  <div className="detail-value detail-text">{forecast.methodology}</div>
                </div>
              )}

              {forecast.assumptions && (
                <div className="detail-item full-width">
                  <label className="detail-label">Assumptions</label>
                  <div className="detail-value detail-text">{forecast.assumptions}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="detail-section full-width">
          <h3 className="detail-section-title">Additional Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label className="detail-label">Created By</label>
              <div className="detail-value">
                {forecast.creator ? (
                  `${forecast.creator.first_name} ${forecast.creator.last_name}`
                ) : (
                  '-'
                )}
              </div>
            </div>

            <div className="detail-item">
              <label className="detail-label">Created At</label>
              <div className="detail-value">{formatDate(forecast.created_at)}</div>
            </div>

            {forecast.reviewer && (
              <>
                <div className="detail-item">
                  <label className="detail-label">Reviewed By</label>
                  <div className="detail-value">
                    {`${forecast.reviewer.first_name} ${forecast.reviewer.last_name}`}
                  </div>
                </div>

                {forecast.reviewed_at && (
                  <div className="detail-item">
                    <label className="detail-label">Reviewed At</label>
                    <div className="detail-value">{formatDate(forecast.reviewed_at)}</div>
                  </div>
                )}
              </>
            )}

            {forecast.notes && (
              <div className="detail-item full-width">
                <label className="detail-label">Notes</label>
                <div className="detail-value detail-text">{forecast.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastView;

